import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'
import Styles from './DraggableShape.module.scss'

import { DraggableShapeProps } from './Types'
import { CSSProperties, DragEventHandler, useMemo, useState } from 'react'
import { autoUpdate, offset } from '@floating-ui/dom'
import { VecLike } from '@tldraw/tldraw'
import { useFloating } from '@floating-ui/react-dom'
import { FrameShape } from 'components/VisualStudioPlayground/Shapes/FrameShape/Types'
import { MaskShape } from 'components/VisualStudioPlayground/Shapes/MaskShape/Types'

const DraggableShape = ({
  children,
  payload,
  dragImage: DragImage,
  draggableEl,
  className,
  isDisabled = false,
}: DraggableShapeProps) => {
  const editor = useEditor()
  const draggableElWidth = draggableEl?.getBoundingClientRect().width || 0

  const [dragging, setDragging] = useState(false)
  // The drag offset is the offset from the top left of the shape to the mouse at the start of the drag.
  const [dragOffset, setDragOffset] = useState<VecLike>({ x: 0, y: 0 })

  const { x, y, refs } = useFloating({
    open: dragging,
    strategy: 'fixed',
    placement: 'bottom-start',
    middleware: [
      offset({
        mainAxis: -dragOffset.y,
        crossAxis: -dragOffset.x,
      }),
    ],
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        animationFrame: true,
      })
    },
  })

  // Set a transparent image as the drag image so that the default drag image
  // doesn't show up. This is necessary because the default drag image is
  // rendered on the GPU and is not affected by CSS transforms, so we'll use our
  // own instead.
  const invisibleDragImage = useMemo(() => {
    new Image()
    let img = new Image(1, 1)
    img.src =
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=='
    img.style.visibility = 'hidden'
    return img
  }, [])

  const handleDragStart: DragEventHandler = (ev) => {
    ev.dataTransfer.setDragImage(invisibleDragImage, 0, 0)
    ev.dataTransfer.setData('payload', JSON.stringify(payload))

    refs.setReference({
      getBoundingClientRect() {
        return {
          width: 0,
          height: 0,
          x: ev.clientX,
          y: ev.clientY,
          top: ev.clientY,
          left: ev.clientX,
          right: ev.clientX,
          bottom: ev.clientY,
        }
      },
    })
    const dragOffset = {
      x: ev.nativeEvent.offsetX,
      y: ev.nativeEvent.offsetY,
    }
    ev.dataTransfer.setData('dragOffset', JSON.stringify(dragOffset))
    setDragOffset(dragOffset)
    setDragging(true)
  }

  const handleDrag: DragEventHandler = ({ clientX, clientY }) => {
    refs.setReference({
      getBoundingClientRect() {
        return {
          width: 0,
          height: 0,
          x: clientX,
          y: clientY,
          top: clientY,
          left: clientX,
          right: clientX,
          bottom: clientY,
        }
      },
    })

    if (payload.type === 'image') {
      const dropPoint = editor.screenToPage({ x: clientX + dragOffset.x, y: clientY + dragOffset.y })
      const targetMask = editor.getShapesAtPoint(dropPoint).find((s): s is MaskShape => s.type === 'mask')

      if (targetMask) {
        editor.setHintingShapes([targetMask])
      } else {
        editor.setHintingShapes([])
      }
    }
  }

  const handleDragEnd = () => {
    refs.setReference(null)
    setDragging(false)
    setDragOffset({ x: 0, y: 0 })
    editor.setHintingShapes([])
  }

  let styles: CSSProperties = {
    width: draggableElWidth,
  }

  if (dragging && x !== null && y !== null) {
    styles.translate = `${x}px ${y}px`

    const dropPoint = editor.screenToPage({ x: x + dragOffset.x, y: y + dragOffset.y })
    const targetFrame = editor.getShapesAtPoint(dropPoint).find((s): s is FrameShape => s.type === 'frame')

    if (targetFrame) {
      const zoomLevel = editor.getZoomLevel()
      // We zoom svgs by scaling the container width instead of using `scale` to avoid blurry edges.
      if (payload.type === 'geometric') {
        styles.width = `${payload.props.w * zoomLevel}px`
      } else {
        styles.scale = `${(payload.props.w / draggableElWidth) * zoomLevel}`
      }

      if (payload.type === 'image') {
        const targetMask = editor.getShapesAtPoint(dropPoint).find((s): s is MaskShape => s.type === 'mask')
        if (targetMask) {
          styles.opacity = 0.5
        } else {
          styles.opacity = 1
        }
      }
    }
  }

  return (
    <>
      {refs.reference && (
        <div className={Styles.dragGhost} ref={refs.setFloating} style={styles}>
          {dragging && <DragImage />}
        </div>
      )}
      <div
        draggable={!isDisabled}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        className={className}
        style={{ opacity: dragging ? 0 : 1 }}
      >
        {children}
      </div>
    </>
  )
}

export { DraggableShape }
